import { useState } from "react"
import { toast } from "sonner"
import { Download, FolderOpen, Upload } from "lucide-react"
import { Button } from "@/modules/shared/ui/components/button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/modules/shared/ui/components/table"
import { useCandidateDocuments } from "../../hooks/useCandidateDocuments"
import { UploadDocumentDialog } from "./UploadDocumentDialog"

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface CandidateDocumentsTabProps {
  candidateId: string
}

export function CandidateDocumentsTab({
  candidateId,
}: CandidateDocumentsTabProps) {
  const [uploadOpen, setUploadOpen] = useState(false)
  const { documents, isLoading, error, downloadDocument } =
    useCandidateDocuments(candidateId)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Spinner />
      </div>
    )
  }

  if (error) {
    toast.error("Failed to load documents")
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        Failed to load documents.
      </p>
    )
  }

  return (
    <Card className="bg-card shadow-md border-border overflow-hidden">
      <CardHeader className="bg-linear-to-r from-primary/5 to-transparent border-b border-border pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg text-foreground flex items-center gap-2">
            <div className="p-1.5 bg-primary/10 rounded-full">
              <FolderOpen className="w-4 h-4 text-primary" />
            </div>
            Documents
          </CardTitle>
          <Button
            size="sm"
            onClick={() => setUploadOpen(true)}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Document
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-5">
        {documents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <div className="bg-muted/50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <FolderOpen className="w-8 h-8 text-muted-foreground/50" />
            </div>
            <p className="font-medium text-foreground">No documents yet</p>
            <p className="text-sm mt-1">Upload a document to get started.</p>
          </div>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>File Name</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Uploaded At</TableHead>
                  <TableHead>Uploaded By</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell className="font-medium">
                      {doc.fileName}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatBytes(doc.size)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(doc.createdAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {doc.createdByName}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          try {
                            const result = await downloadDocument(doc.id)
                            const url = result?.sasUrl
                            if (!url) {
                              toast.error("Download URL not available")
                              return
                            }
                            const openedWindow = window.open(
                              url,
                              "_blank",
                              "noopener,noreferrer",
                            )
                            if (!openedWindow) {
                              toast.error(
                                "Could not open document. Please allow pop-ups and try again.",
                              )
                            }
                          } catch {
                            toast.error("Failed to open document")
                          }
                        }}
                      >
                        <Download className="w-3 h-3 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <UploadDocumentDialog
        candidateId={candidateId}
        open={uploadOpen}
        onOpenChange={setUploadOpen}
      />
    </Card>
  )
}
